import React from 'react';
import { IFetchDataState } from '../../../../store/IFetchDataState';
import { ISelectOption, ISpruceFieldProps, Select, SpruceField } from 'ui-toolkit';
import { FormikContextType } from 'formik';
import { PreExistingContactSelect } from '../subComponents/PreExistingContactSelect';
import { ILenderUserData } from '../../../../types/lenders/ILenderUser';
import { activeLenderContactsFunctionsOptions } from '../../../../types/contacts/activeLenderContactsFunctionsOptions';
import { useListContactFunctionsQuery } from '../../../../API/contacts/ContactFunctionsApi';
import { getContactFunctionValue } from '../../../../utilities/contact/getContactFunctionValue';
import { OrderContactNotesField } from '../subComponents/OrderContactNotesField';
import { ErrorBoundaryConstellation } from '../../../../constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';

interface IAddPreExistingLenderOrderContactFormProps {
  preExistingLenderContacts: IFetchDataState<string, ILenderUserData[]>;
  onAddContact: (email: string) => void;
}

export const AddPreExistingLenderOrderContactForm: React.FC<IAddPreExistingLenderOrderContactFormProps> = ({
  preExistingLenderContacts,
  onAddContact,
}) => {
  const isFetching = preExistingLenderContacts.fetchStatus.fetching;
  const { data: contactFunctions = [] } = useListContactFunctionsQuery();

  const lenderContactOptions: ISelectOption[] = preExistingLenderContacts.data.map((contact) => ({
    label: `${contact.firstName} ${contact.lastName}`,
    value: contact.id,
  }));

  const handleChange = (formik: FormikContextType<any>, option?: ISelectOption): void => {
    if (!!option) {
      const contact = lenderContactFromId(option.value);
      if (!!contact) {
        onAddContact(contact.id);
        // Use setValues instead of multiple setFieldValue calls for better performance and proper dirty flag update
        formik.setValues({
          ...formik.values,
          id: contact.id,
          email: contact.email,
          firstName: contact.firstName,
          lastName: contact.lastName,
          phoneNumber: contact.phoneNumber,
          functions: contact.functions,
        });
      }
    } else {
      // Clear all fields
      formik.setValues({
        ...formik.values,
        id: '',
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        functions: [],
      });
    }
  };

  const lenderContactFromId = (contactId: string): ILenderUserData | undefined =>
    preExistingLenderContacts.data.find((contact) => contact.id === contactId);

  return (
    <ErrorBoundaryConstellation>
      <PreExistingContactSelect
        isFetching={isFetching}
        options={lenderContactOptions}
        handleChange={handleChange}
        label={'An existing lender contact'}
      />
      <SpruceField name={'functions'}>
        {({ field }: ISpruceFieldProps) => (
          <Select
            {...field}
            onChange={(values: ISelectOption[]) => field.onChange(values.map((value) => value.value))}
            value={getContactFunctionValue(field.value, contactFunctions)}
            options={activeLenderContactsFunctionsOptions(contactFunctions)}
            isClearable={true}
            isMulti={true}
            isRequired
            label={'Function(s)'}
          />
        )}
      </SpruceField>
      <OrderContactNotesField name={'notes'} />
    </ErrorBoundaryConstellation>
  );
};
